// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { WebThingPort, WebThing } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";

import { PortKeys, TextEditorData } from './TextEditorStructs';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
const TextEditorComponent = React.lazy(() => import('./TextEditorAsync'));
const TextEditorSettings = React.lazy(() => import('./TextEditorAsync').then(module => ({ default: module.TextEditorSettings })));

// =================================================================================================
export function TextInputValidator() {
    return JsonSchemaValidator.GetInstance(
        'TextInputDataPortValidator',
        {
            settings: {
                type: "object",
                properties: {
                    //Define Everything that have to do with the editor settings
                    editorSettings: {
                        type: ["object"],
                        properties: {
                            showToolbar: { type: "boolean" }
                        }
                    },
                    //Define Editor Settings that have to do with the content here
                    rawEditorStateData: {
                        type: ["object"],
                        properties: {
                            blocks: {
                                type: "array", items: {
                                    type: ["object"],
                                    properties: {
                                        key: { type: "number" },
                                        text: { type: "string" },
                                        type: { type: "string" },
                                        depth: { type: "number" },
                                        inlineStylesRanges: {
                                            type: "array", items: {
                                                type: ["object"],
                                                properties: {
                                                    offset: { type: "number" },
                                                    length: { type: "number" },
                                                    style: { type: { enum: ['BOLD', 'CODE', 'ITALIC', 'STRIKETHROUGH', 'UNDERLINE'] } }
                                                }
                                            }
                                        },//inline end
                                        entityRanges: {
                                            type: "array", items: {
                                                type: ["object"],
                                                properties: {
                                                    key: { type: "number" },
                                                    offset: { type: "number" },
                                                    length: { type: "number" }
                                                }
                                            }
                                        }//inline end
                                        //TODO data need to be added here
                                    }//props end
                                }//items end
                            },//block end
                            entityMap: {
                                type: ["object"],
                                properties: {
                                    "^[0-9]+$": {
                                        type: ["object"],
                                        properties: {
                                            RawDraftEntity: {
                                                type: ["object"],
                                                properties: {
                                                    type: { type: "string" },
                                                    mutability: { type: { enum: ['MUTABLE', 'IMMUTABLE', 'SEGMENTED'] } },
                                                    data: {
                                                        type: ["object"],
                                                        properties: {
                                                            src: { type: "string" },
                                                            height: { type: "string" },
                                                            width: { type: "string" }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }

                            },
                            //TODO Entity map validation need to be added here
                        }//propend
                    }//rawEditorStateData end
                }//prop end
            }//settings end
        }
    );
}
// =================================================================================================
export class TextEditorWidget extends IWidgetThingsClass<TextEditorData> {
    Render(): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <TextEditorComponent />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        const options = this.WidgetData;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <TextEditorSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(options)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: TextEditorData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: 'Input Data',
                PortKey: PortKeys.InputData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'PortKeyDescription',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.InputData, data))
        );
        ports.push(new WebThingPort(
            {
                Name: "Output Data",
                PortKey: PortKeys.OutputData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The selected pie chart data',
                State: ""
            },
            WebThingPortType.Trigger
        ));
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = this.WidgetData;

        // Create plegma thing.
        let plegmaThing: Plegma.Thing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TextEditorData {
        return {

            showToolbar: true
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return TextEditorWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: TextEditorData = this.GetDefaultWidgetDataStatic();
        return {
            Title: "Editor",
            WidgetTitle: "",
            Key: "TextEditorWidget",
            Type: "TextEditorWidget",
            Data: data,
            Description: "EditorWidget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/line_chart.svg'
        };
    }

    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        switch (key) {
            case PortKeys.InputData:
                return TextInputValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: TextEditorData): TextEditorData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues(): { [key: string]: any } {
        return {
            [PortKeys.InputData]: {
            }
        };
    }
    // ---------------------------------------------------------------------------------------------
}